import React from "react";
import { useTranslation } from "react-i18next";
import { InfoCircle } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import Text from "@/components/ui/text";
import { formatCurrency } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { useCurrencyConversion } from "@/services/hooks/useCurrencyConversion";
import { DealFinancialEstimate } from "./financials";

interface Props {
  estimate: DealFinancialEstimate;
  currency: string;
}

const DealFinancialsFooter: React.FC<Props> = ({ estimate, currency }) => {
  const { t } = useTranslation();
  const { isArr } = useArrMrr();
  const { accountCurrency, showConversion, rate } =
    useCurrencyConversion(currency);

  const recurringValue = isArr ? estimate.arr : estimate.mrr;
  const rows = [
    {
      key: "recurring",
      label: t(isArr ? "contractTerms.arr" : "contractTerms.mrr"),
      value: recurringValue,
      tooltip: t(isArr ? "contractTerms.arrFull" : "contractTerms.mrrFull")
    },
    {
      key: "tcv",
      label: t("contractTerms.tcv"),
      value: estimate.tcv,
      tooltip: t("contractTerms.tcvFull")
    }
  ];

  return (
    <div
      className="flex flex-col gap-1 border-t-[0.5px] border-border-border1 px-6 py-4"
      data-test="create-deal-financials"
    >
      {rows.map((row) => (
        <div key={row.key} className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Text variant="bodySecondary">{row.label}</Text>
            <IconButton
              variant="iconNoBg"
              size="sm"
              iconColor="tertiary"
              tooltipText={row.tooltip}
              aria-label={row.tooltip}
            >
              <InfoCircle />
            </IconButton>
          </div>
          <div className="flex items-center gap-1">
            <Text variant="body" data-test={`create-deal-${row.key}`}>
              {formatCurrency(row.value, currency)}
            </Text>
            {showConversion && rate && row.value > 0 ? (
              <Text variant="bodyTertiary">
                {`(${formatCurrency(row.value * rate, accountCurrency, 0)})`}
              </Text>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DealFinancialsFooter;
